import styles from './WidgetContentModal.module.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '../Loader/Loader';
import { ampli } from '../../ampli';
import cn from 'classnames';
import { ExpandableText } from '../CollapsibleText/ExpandableText';
import { ReactComponent as DeleteIcon } from '../../assets/delete.svg';
import { ReactComponent as ChevronIcon} from '../../components/Filters/assets/chevron.svg';
import { ReactComponent as SortIcon } from '../../assets/sort.svg';
import { Radiobutton } from '../DropdownMenu/Radiobutton';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { replaceNewlinesWithBreaks } from '../../utils/replaceNewlinesWithBreaks';
import { ImagesContainer } from '../Image/ImagesContainer';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { TranslationSwitch } from '../TranslationSwitch/TranslationSwitch';
import { formatNumber } from '../../utils/fomatNumbers';
import { OriginalTextBlock } from '../MessagesTable/OriginalTextBlock';
import { MessageMetricsWrapper } from '../MessagesTable/MessageMetricsWrapper';
import  { MessageInfoWrapper } from '../MessagesTable/MessageInfoWrapper';
import { MessageTooltips } from '../MessagesTable/MessageTooltips';

import { LoaderSmall } from '../LoaderSmall/LoaderSmall';

import messageStyles from "../MessagesTable/ExtendedMessageView.module.scss";
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';


const sortingFieldNames = {
  DATE: 'date_publicated',
  SOURCE: 'source',
  MESSAGE: 'text',
  VIEWS: 'impressions',
  REACTIONS: 'engagement',

  AGE: 'user_age',
  LOCATION: 'user_location',
  GROUPS: 'user_groups',
};

export const ModalMessagesTable = ({
  messages,
  sorting,
  handleMessagesSortingClick,
  isLoading,
  isShare = false,
  handleTranslation,
  narrative,
  event,
  opinion,
  onMessageDelete,
  isBulkTranslation,
  setIsBulkTranslation,
  isBulkTranslationLoading,
  handleBulkTranslateClick,
  handleShowOriginalClick,
  showSortingInTable = true,
  showLoadMore = false,
  isLoadingMore = false,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [isMessageDropdownOpen, setIsMessageDropdownOpen] = useState({});
  const [dropdownMenu, setDropdownMenu] = useState(false);

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showSiilaritySearch = featureFlags?.includes('show_similarity_search');

  const statusMapping = {
    NEW: {class: messageStyles.new, name: 'New'},
    READ: {class: messageStyles.approved, name: 'Approved'},
    DELETED: {class: messageStyles.deleted, name: 'Deleted'}
  }
  
  const [highlights, setHighlights] = useState(['KEYWORDS']);

  const handleDelete = (messageId) => {
    if(!opinion) {
      API.fetch('DELETE', `/API/v1/topic-modeling/${narrative.id}/events/${event.id}/messages/${messageId}`).then(() => {
        onMessageDelete()
      }).catch((e) => showRequestError(e, t('Delete publication')));
    } else {
      API.fetch('DELETE', `/API/v1/topic-modeling/${narrative.id}/events/${event.id}/opinions/${opinion.id}/messages/${messageId}`).then(() => {
        onMessageDelete()
      }).catch((e) => showRequestError(e, t('Delete publication')));
    }
  };


  const sortingMenu = [
    {
      name: t('Newest'),
      value: '-date_publicated',
    },
    {
      name: t('Oldest'),
      value: 'date_publicated',
    },
    {
      name: t('Most viewed'),
      value: '-impressions',
    },
    {
      name: t('Least viewed'),
      value: 'impressions',
    },
     {
      name: t('Highest engagement'),
      value: '-engagement',
    },
    
  ];

 
  return (
    <>
      {isLoading ? <Loader /> : (messages?.objects?.length ?? 0) > 0 ? (
        <>
        <table className={messageStyles.extendedMessages} id='widgetContentModal'>
        <thead>
          <tr>
            <td className={messageStyles.headerInfo}>
              <span>{t('Total publications')}: {formatNumber(messages.total)}</span>
            </td>
            <td className='message-controlls'>
              {showSortingInTable && <div
                className={messageStyles.sortingWrapper}
                style={{ position: 'absolute' }}
              >
                <DropdownMenu
                  isOpen={dropdownMenu}
                  onClick={() => setDropdownMenu(!dropdownMenu)}
                  menuClassName="searchSorting"
                  buttonName={`${t('Sort by')}: ${t(
                    sortingMenu.find(
                      (item) => item.value === sorting,
                    )?.name || '',
                  )}`}
                  icon={<ChevronIcon />}
                  frontIcon={<SortIcon />}
                >
                  <Radiobutton
                    itemsList={sortingMenu}
                    current={sorting}
                    onChange={(value) => {
                      handleMessagesSortingClick(value);
                      setDropdownMenu(false);
                    }}
                  />
                </DropdownMenu>
              </div>}
              <div className={messageStyles.translationSwitchWrapper}>
                <TranslationSwitch
                  isBulkTranslation={true}
                  isTranslation={isBulkTranslation}
                  isLoading={isBulkTranslationLoading}
                  theme='dark'
                  size='medium'
                  handleTranslation={(bool) => {
                    if (bool) {
                      setIsBulkTranslation(bool);
                      handleBulkTranslateClick();
                      ampli.track({
                        event_type: 'Bulk Translation Click',
                        event_properties: {
                          user_id: currentUser?.id,
                          workspace_id: currentUser?.workspace_id,
                          narrative_id: narrative?.id,
                          event_location: 'Widget Content Modal',
                          TM_event_id: event?.id,
                          TM_opinion_id: opinion?.id,
                        },
                      });
                    } else {
                      setIsBulkTranslation(false);
                      handleShowOriginalClick();
                    }
                  }}
                />
              </div>
            </td>
          </tr>
        </thead>
        <tbody>
          {messages.objects.map(message => {
            let followers_cnt = message?.source?.audience;
            let textClass = messageStyles.extendedMessage;
            let textClassParent = messageStyles.extendedMessage;

            if (message.parent_message?.error && message.parent_message?.isTranslation) {
                textClassParent = `${messageStyles.extendedMessage} ${messageStyles.error}`
              } else if (message.error && !message.isTranslation) {
                textClassParent = messageStyles.extendedMessage;
              }
  
            if(message.error && message.isTranslation) {
              textClass = `${messageStyles.extendedMessage} ${messageStyles.error}`
            } else if (message.error && !message.isTranslation) {
              textClass = messageStyles.extendedMessage;
            }

            let messageMenuOptions = []
            if(showSiilaritySearch) {
              messageMenuOptions.push({
                name: t('Find similar publications'),
                value: 'similar'
              })
            }

            if((message?.content_type === 'COMMENT' && (message?.parent_message || message?.parent_id)) || message?.content_type === 'POST') {
              messageMenuOptions.push({
                name: t('Find comments to post'),
                value: 'comments'
              })
            }

            if (message?.content_type === 'VIDEO' && message?.source?.source_type === 'TIKTOK') {
              messageMenuOptions.push({
                name: t('Find comments to video'),
                value: 'comments_to_video'
              })
            }

            if (message?.content_type === 'POST' && ['TIKTOK', 'FACEBOOK', 'TELEGRAM'].includes(message?.source?.source_type)) {
              messageMenuOptions.push({
                name: t('Find reposts to post'),
                value: 'reposts_to_post'
              })
            }

            const textForTranslation = message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.text);
  
            return (
              <tr key={message.id}>
                <td>
                  <MessageInfoWrapper
                    message={message}
                    followers_cnt={followers_cnt}
                    isShare={isShare}
                    currentUser={currentUser}
                    narrative={narrative}
                    narrartiveMessage={null}
                    setMessageModal={null}
                  />

                  <div className={messageStyles.messageContainer}>
                    <MessageMetricsWrapper
                      message={message}
                      narrative={narrative}
                      isShare={isShare}
                      isMessageDropdownOpen={isMessageDropdownOpen}
                      setIsMessageDropdownOpen={setIsMessageDropdownOpen}
                      messageMenuOptions={messageMenuOptions}
                    />

                    {message.parent_message ? (
                      <OriginalTextBlock
                        message={message}
                        isShare={isShare}
                        currentUser={currentUser}
                        narrative={narrative}
                        textClassParent={textClassParent}
                      />
                    ) : (
                      ''
                    )}

                    {message?.images && message?.images?.length > 0 ? (
                      <ImagesContainer images={message?.images} showCount={3} className={styles.imagesContainer}/>
                    ) : (
                      ''
                    )}

                    <div className={messageStyles.textBlock}>
                      {message.isTranslation === true && !message.translated ? (
                        <div className={messageStyles.loaderContainer}>
                          <LoaderSmall />
                        </div>
                      ) : (
                        <ExpandableText
                          length={150}
                          highlights={highlights}
                          text={
                            message.isTranslation
                              ? message.translated
                              : message.highlighted_text || message.text
                          }
                          textClassName={textClass}
                        />
                      )}
                    </div>
                  </div>
                </td>
                <td className="message-controlls">
                  {currentUser?.is_super_admin && (event || opinion) ? (
                    <div className={messageStyles.controls}>
                      <button
                        className={`${messageStyles.delete} btn-danger`}
                        onClick={() => handleDelete(message.id)}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  ) : (
                    ''
                  )}

                  <div className={messageStyles.translationWrapperNew}>
                      <TranslationSwitch
                        isTranslation={message.isTranslation}
                        theme='light'
                        size='medium'
                        handleTranslation={(bool) => {
                          handleTranslation(
                            message.id,
                            textForTranslation,
                            bool
                          )
                        }}
                      />

                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {showLoadMore && (
        <button
          type="button"
          className={cn(styles.loadMore, { [styles.loading]: isLoadingMore })}
          disabled={isLoadingMore}
          aria-busy={isLoadingMore}
          onClick={onLoadMore}
        >
          {isLoadingMore && <span className={styles.loadMoreSpinner} aria-hidden="true" />}
          {isLoadingMore ? t('Loading...') : t('Load more')}
        </button>
      )}
      </>
      ) : (
        <div className={messageStyles.noData}>{t('There is no data to display')}</div>
      )}
      <MessageTooltips scope="#widgetContentModal" />
    </>
  );
};
